<script setup lang="ts">
import { ref } from 'vue'

interface ToastMessage {
    id: number
    message: string
    actionLabel?: string
    actionHandler?: () => void
}

const toasts = ref<ToastMessage[]>([])
let nextId = 0
interface ToastOptions {
    durationMs?: number
    action?: { label: string; handler: () => void }
}

const timers = new Map<number, ReturnType<typeof setTimeout>>()

function show(message: string, options?: number | ToastOptions): void {
    const id = nextId++
    const opts: ToastOptions =
        typeof options === 'number' ? { durationMs: options } : (options ?? {})
    const duration = opts.durationMs ?? 2400

    function dismiss(): void {
        toasts.value = toasts.value.filter((t) => t.id !== id)
        const timer = timers.get(id)
        if (timer) {
            clearTimeout(timer)
            timers.delete(id)
        }
    }

    const actionHandler = opts.action
        ? () => {
              opts.action!.handler()
              dismiss()
          }
        : undefined

    toasts.value = [
        ...toasts.value,
        {
            id,
            message,
            actionLabel: opts.action?.label,
            actionHandler,
        },
    ]

    timers.set(id, setTimeout(dismiss, duration))
}

defineExpose({ show })

export type ToastApi = {
    show: (
        message: string,
        options?: number | { durationMs?: number; action?: { label: string; handler: () => void } },
    ) => void
}
</script>

<template>
    <Teleport to="body">
        <div
            v-for="(toast, index) in toasts"
            :key="toast.id"
            aria-live="polite"
            role="status"
            class="toast show"
            :style="{ bottom: `${22 + index * 52}px` }"
        >
            <span>{{ toast.message }}</span>
            <button
                v-if="toast.actionLabel"
                type="button"
                class="ml-2 font-bold underline underline-offset-2"
                @click="toast.actionHandler?.()"
            >
                {{ toast.actionLabel }}
            </button>
        </div>
    </Teleport>
</template>
